import React, { useState, useRef, useEffect } from "react";

export interface HeadlessSelectOption {
  value: string | number;
  label: string;
}

export interface HeadlessSelectProps {
  id?: string;
  name?: string;
  value?: string | number;
  onChange?: (e: { target: { name?: string; value: string | number } }) => void;
  options: HeadlessSelectOption[];
  placeholder?: string;
  className?: string;
  error?: boolean;
  disabled?: boolean;
}

const HeadlessSelect: React.FC<HeadlessSelectProps> = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder,
  className = "",
  error = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className="relative z-20" ref={dropdownRef}>
      <button
        type="button"
        id={id}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`block w-full min-h-[48px] px-3 py-2 text-left bg-light-surfaceSecondary dark:bg-secondary-dark border border-light-border-default dark:border-hover-dark rounded-xl shadow-sm transition-all duration-200 hover:border-light-border-interactive hover:shadow-md focus:ring-2 focus:ring-primary-light focus:border-primary-light dark:focus:ring-primary-dark dark:focus:border-primary-dark ${
          disabled ? "cursor-not-allowed opacity-70" : ""
        } ${className} ${
          error
            ? "border-light-status-error-border ring-2 ring-light-status-error-bg dark:border-red-500"
            : ""
        }`}
      >
        <div className="flex items-center justify-between">
          <span
            className={`text-sm ${
              selectedOption
                ? "text-light-text-primary dark:text-white"
                : "text-light-text-muted dark:text-gray-400"
            }`}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg
            className={`w-5 h-5 text-light-text-secondary dark:text-gray-400 transition-transform duration-200 ${
              isOpen ? "transform rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {isOpen && !disabled && (
        <div className="absolute w-full mt-1 bg-light-surfaceHover dark:bg-gray-700 border border-light-border-default dark:border-gray-600 rounded-lg shadow-md overflow-hidden animate-in fade-in zoom-in-95 duration-100 z-20">
          <ul className="py-1 max-h-60 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            {options.map((option) => (
              <li
                key={option.value}
                onClick={() => {
                  if (onChange) {
                    onChange({ target: { name, value: option.value } });
                  }
                  setIsOpen(false);
                }}
                className={`px-3 py-2 cursor-pointer transition-colors duration-150 text-sm ${
                  option.value === value
                    ? "bg-light-status-info-bg dark:bg-slate-600 text-light-text-primary dark:text-white"
                    : "text-light-text-primary dark:text-gray-300 hover:bg-light-interactive-hover dark:hover:bg-gray-600"
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HeadlessSelect;